import { getI18n } from '@/plugins/i18n'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useDefaultDialogTitle, useLookup } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

export const setDepositValue = (data, value, currentData, field, otherField, isTransfer) => {
  const previousValue = Object.hasOwn(data, field) ? data[field] : currentData[field]
  const otherValue = Object.hasOwn(data, otherField) ? data[otherField] : currentData[otherField]
  data[field] = value?.id ?? value ?? null
  if (isTransfer && data[field] != null && data[field] === otherValue) {
    data[otherField] = previousValue ?? null
    return true
  }
  return false
}

export const useColumns = (documentType, editors) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const registryStore = useRegistryStore()
  const webSiteStore = useWebSiteStore()
  const { WAREHOUSE_LOAD, WAREHOUSE_UNLOAD, WAREHOUSE_TRANSFER } = portalStore.documentTypesEnum
  const lookup = (values = []) => ({ dataSource: values, valueExpr: 'id', displayExpr: 'name' })
  const { withDocumentSeriesAndNumberMnemonicUpdate } = dataGridColumn.useDocumentSeriesAndNumberMnemonicUpdate()
  const differentDepositRule = otherField => ({
    type: 'custom', reevaluate: true,
    message: i18n.t('error.validation.must_be_different'),
    validationCallback: e => documentType.value !== WAREHOUSE_TRANSFER || !e.value || e.value !== e.data[otherField],
  })

  return [
    { type: 'buttons', buttons: [{template: 'editButtonsTemplate'}], width: '8rem', visibleIndex: 0, fixed: false },
    dataGridColumn.useDateColumn({
      validationRules: [{ type: 'required' }],
    }),
    dataGridColumn.useSeriesColumn({ editorOptions: { readOnly: true } }),
    dataGridColumn.useNumberMnemonicColumn({ editorOptions: { readOnly: true } }),
    {
      dataField: 'document_type', caption: i18n.t('common.documentType'),
      lookup: lookup(portalStore.getLocalizedDocumentTypes().filter(item => [WAREHOUSE_LOAD, WAREHOUSE_UNLOAD, WAREHOUSE_TRANSFER].includes(item.id))),
      validationRules: [{ type: 'required' }],
      setCellValue: withDocumentSeriesAndNumberMnemonicUpdate((data, value, currentData) => {
        data.document_type = documentType.value = value
        currentData.number_mnemonic = null
      }, ['document_type'], editors, registryStore.api.getWarehouseDocumentNextIdByDataAsync),
    },
    {
      dataField: 'portal_warehouse_reason', caption: i18n.t('common.reason'),
      lookup: useLookup({ store: portalStore.getLocalizedWarehouseReasons() }),
    },
    dataGridColumn.useDepositColumn({
      visible: true,
      validationRules: [{ type: 'required' }, differentDepositRule('destination_deposit')],
      setCellValue: (data, value, currentData) => { setDepositValue(data, value, currentData, 'deposit', 'destination_deposit', documentType.value === WAREHOUSE_TRANSFER) },
    }),
    dataGridColumn.useDepositColumn({
      dataField: 'destination_deposit', caption: i18n.t('warehouseDocument.destinationDeposit'), visible: true,
      validationRules: [{ type: 'required' }, differentDepositRule('deposit')],
      setCellValue: (data, value, currentData) => { setDepositValue(data, value, currentData, 'destination_deposit', 'deposit', documentType.value === WAREHOUSE_TRANSFER) },
    }),
    {
      dataField: 'created_by', caption: i18n.t('warehouseDocument.insertedBy'), allowEditing: false,
      lookup: useLookup({
        store: webSiteStore.userDetails,
        valueExpr: 'user_id',
        sort: 'name',
        displayExpr: (item) => item ? `${item.name} ${item.name_ext || ''}`.trim() : '',
      }),
    },
    {
      dataField: 'receipt_channel', caption: i18n.t('warehouseDocument.receiptChannel'),
      lookup: useLookup({ store: portalStore.getLocalizedWarehouseReceiptChannels() }),
    },
    { dataField: 'status', caption: i18n.t('common.status'), allowEditing: false, lookup: lookup(portalStore.getLocalizedDocumentStatuses()) },
    {
      dataField: 'subject_ref', caption: webSiteStore.blankCaption,
      lookup: useLookup({ store: registryStore.getSubjects() }),
      editCellTemplate: 'subjectEditor',
      setCellValue: (data, value) => { data.subject_ref = value?.id || value },
    },
    { dataField: 'reference_number', caption: i18n.t('warehouseDocument.referenceNumber') },
    dataGridColumn.useDateColumn({ dataField: 'reference_date', caption: i18n.t('warehouseDocument.referenceDate') }),
    { dataField: 'notes', caption: i18n.t('common.note', 2), visible: false },
  ]
}

export const useDialogTitle = () => {
  const i18n = getI18n()
  const { defaultDialogTitle } = useDefaultDialogTitle()
  const dialogTitle = data => data.id ? defaultDialogTitle(data, i18n.t('common.document')) : i18n.t('warehouseDocument.newDocument')
  return { dialogTitle }
}
